import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProcessing } from '../contexts/ProcessingContext';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Settings as SettingsIcon, LogOut, CreditCard, Clock, ChevronDown, User, Home, Shield, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export default function Navbar({ onOpenProcessingModal }) {
  const { user, userProfile, logout } = useAuth();
  const { backgroundSession, backgroundProgress, openModal } = useProcessing();
  const navigate = useNavigate();
  const location = useLocation();
  const userMinutes = userProfile?.minutes || 0;

  const handleProcessingClick = () => {
    if (onOpenProcessingModal) {
      onOpenProcessingModal(backgroundSession);
    }
  };

  const getFirstName = () => {
    if (user?.first_name) return user.first_name;
    if (userProfile?.name) return userProfile.name.split(' ')[0];
    return user?.email?.split('@')[0] || 'User';
  };

  const getFullName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) return user.first_name;
    if (userProfile?.name) return userProfile.name;
    return 'User';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          <div className="flex items-center space-x-6">
            <div 
              className="flex items-center space-x-2.5 cursor-pointer" 
              onClick={() => navigate('/dashboard')}
            >
              <img src="/logo.png" alt="Allioki" className="h-8 w-8" />
              <span className="text-xl font-bold bg-gradient-to-r from-primary via-purple-400 to-pink-400 bg-clip-text text-transparent tracking-widest">ALLIOKI</span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-1">
              {!isActive('/dashboard') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                >
                  <Home className="h-4 w-4 mr-2" />
                  My Songs
                </Button>
              )}
            </nav>
          </div>
          
          <div className="flex items-center space-x-2">
            {backgroundSession && backgroundProgress && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-2 px-2 animate-pulse"
                      onClick={handleProcessingClick}
                    >
                      <div className="relative">
                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                      </div>
                      <span className="hidden sm:inline text-xs text-primary font-medium">
                        {backgroundProgress.percent || 0}%
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Processing in background - click to view</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    {user.profile_image_url ? (
                      <img 
                        src={user.profile_image_url} 
                        alt={user.first_name || 'User'} 
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">
                      {getFirstName()}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{getFullName()}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      {userProfile?.screen_name && (
                        <p className="text-xs text-primary">@{userProfile.screen_name}</p>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Song Minutes</span>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-primary">{userMinutes.toFixed(1)} min</span>
                      </div>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  
                  <div className="md:hidden">
                    {!isActive('/dashboard') && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/dashboard')}
                        className="cursor-pointer"
                      >
                        <Home className="h-4 w-4 mr-2" />
                        <span>My Songs</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </div>
                  
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')}
                    className="cursor-pointer"
                  >
                    <CreditCard className="h-4 w-4 mr-2 text-primary" />
                    <span>Get More Minutes</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')}
                    className="cursor-pointer"
                  >
                    <SettingsIcon className="h-4 w-4 mr-2" />
                    <span>Account Settings</span>
                  </DropdownMenuItem>
                  {user?.is_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')}
                        className="cursor-pointer"
                      >
                        <Shield className="h-4 w-4 mr-2 text-amber-500" />
                        <span>Admin Panel</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="cursor-pointer text-red-500 focus:text-red-500"
                    data-testid="logout-button"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
